import { useEffect, useLayoutEffect, useRef } from "react";

import type { Locale, Message } from "../../../shared/types";
import { useCopy } from "../lib/copy";

interface TranscriptProps {
  locale: Locale;
  /** Language of this conversation, so a screen reader picks the right voice per turn. */
  language: Locale;
  turns: Message[];
}

/** Within this many pixels of the end, a new turn may pull the page down. */
const NEAR_BOTTOM = 160;

/** The two scripts this product speaks in. */
const CYRILLIC = /[Ѐ-ӿ]/;
const LATIN = /[A-Za-z]/;

/**
 * Which language a turn is in.
 *
 * The conversation carries one language, and every `<li>` used to be stamped with it —
 * so a Russian sentence typed into an English conversation was announced by an English
 * voice, letter by letter. There is no per-message language on the wire and inventing
 * a column for one would be a migration for a hint; the script the words are written
 * in is the honest answer and it is right in both directions.
 */
function speechLanguage(content: string, fallback: Locale): Locale {
  if (CYRILLIC.test(content)) return "ru";
  if (LATIN.test(content)) return "en";
  // "40" is neither, and reading it in the language of the room is right.
  return fallback;
}

export default function Transcript({ locale, language, turns }: TranscriptProps) {
  const copy = useCopy(locale);
  const nearBottom = useRef(false);
  const seen = useRef(turns.length);
  // Anything already on screen at mount has settled. Only what arrives later fades in,
  // so a reload does not animate the whole archive.
  const baseline = useRef(turns.reduce((highest, turn) => Math.max(highest, turn.id), 0));

  // Measured continuously rather than at append time: once the new turn is in the DOM
  // the page is already taller and the answer to "were they at the end?" is gone.
  useEffect(() => {
    const measure = (): void => {
      const doc = document.scrollingElement ?? document.documentElement;
      nearBottom.current = doc.scrollHeight - doc.scrollTop - doc.clientHeight < NEAR_BOTTOM;
    };
    measure();
    window.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, []);

  // A conversation arrives one render after mount, so `turns` goes 0 -> N and looks
  // exactly like a live turn landing. Following it would scroll the page to the
  // oldest thing in the archive at the moment of arrival, when what the founder
  // wants to see is the question the consultant is asking right now, up top.
  const hydrated = useRef(false);

  useLayoutEffect(() => {
    const grew = turns.length > seen.current;
    seen.current = turns.length;
    if (!hydrated.current) {
      hydrated.current = turns.length > 0;
      return;
    }
    if (!grew || !nearBottom.current) return;
    const doc = document.scrollingElement ?? document.documentElement;
    // Never smooth: the transcript is not animated once someone is reading it.
    window.scrollTo({ top: doc.scrollHeight, behavior: "auto" });
  }, [turns.length]);

  if (turns.length === 0) return null;

  return (
    <ol className="transcript" aria-live="off">
      {turns.map((turn) => {
        const them = turn.role === "assistant";
        const settling = turn.id > baseline.current;
        return (
          <li
            key={turn.id}
            className={[
              "turn",
              them ? "turn--them" : "turn--you",
              settling ? "turn--settling" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            lang={speechLanguage(turn.content, language)}
          >
            <span className="sr-only">{them ? copy.speakerThem : copy.speakerYou}</span>
            {turn.content
              .split(/\n+/)
              .filter((part) => part.trim().length > 0)
              .map((part, position) => (
                <p
                  key={position}
                  style={position === 0 ? { margin: 0 } : { margin: "var(--s5) 0 0" }}
                >
                  {part}
                </p>
              ))}
          </li>
        );
      })}
    </ol>
  );
}
